
import React, { useState, useEffect, useRef, useMemo } from "react";
import { PublicItems, PublicAppSettings, PublicInventoryMovement, PublicPromoGroup, PublicCheckout, PublicStockBalance, PublicProductComponent, PublicWaitlist, PublicTargetMap } from "@/api/publicEntities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  ShoppingCart,
  Plus,
  Minus,
  BookOpen,
  Search,
  Mail,
  ChevronLeft,
  ChevronRight,
  Package,
  Heart,
  Truck,
  Award,
  Shield,
  Zap,
  Smile,
  ThumbsUp,
  Feather,
  Globe
} from "lucide-react";

import CheckoutModal from "../components/store/CheckoutModal";
import { parseHeroBlocks } from '@/utils/heroDescription.js';
import ProductLabels from "../components/store/ProductLabels";
import Footer from "@/components/ui/Footer";
import TargetMapOverlay from "@/components/store/TargetMapOverlay";
import WaitlistSubscribeDialog from "@/components/store/WaitlistSubscribeDialog";
import { trackEvent } from "@/lib/analytics";

// Helper to safely format currency values
const formatCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0';
  return numeric.toLocaleString();
};

const defaultStoreSettings = {
  store_title: "BookForge Store",
  store_tagline: "Качественные книги для души",
  hero_title: "Откройте новые миры через книги",
  hero_description: "Качественная литература от независимого издательства. Каждая книга - это путешествие, которое изменит вас.",
  primary_color: "#f59e0b",
  gradient_color_from: "#fffbeb",
  gradient_color_to: "#fef3c7",
  font_family: "inter",
  footer_text: "Создаем и издаем качественную литературу для думающих читателей",
  footer_copyright: "© 2024 BookForge Publishing. Все права защищены.",
  button_background: "#f59e0b",
  button_text_color: "#ffffff",
  button_hover_background: "#d97706",
  hero_features: [
    {title: "Быстрая доставка", description: "Доставляем по всей России в кратчайшие сроки", icon: "Package"},
    {title: "Высокое качество печати", description: "Только лучшие материалы и технологии", icon: "Star"},
    {title: "Тщательный отбор произведений", description: "Каждая книга проходит строгий отбор", icon: "Heart"}
  ],
  storefront_warehouse_ids: []
};

const fontClassMap = {
  'inter': 'font-inter',
  'roboto': 'font-roboto',
  'open-sans': 'font-open-sans',
  'lato': 'font-lato',
  'poppins': 'font-poppins',
  'merriweather': 'font-merriweather',
  'bahnschrift': 'font-bahnschrift',
  'ubuntu': 'font-ubuntu',
  'manrope': 'font-manrope'
};

const iconMap = {
  Package, Heart, Truck, BookOpen, Award, Shield, Zap, Smile, ThumbsUp, Feather, Globe
};

// Хук для детекции телефонного разрешения по отношению сторон
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const aspectRatio = height / width;
    // Телефон: ширина < 768px ИЛИ портретная ориентация с aspect ratio > 1.3
    return width < 768 || aspectRatio > 1.3;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const updateIsMobile = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = height / width;
      setIsMobile(width < 768 || aspectRatio > 1.3);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    window.addEventListener('orientationchange', updateIsMobile);
    
    return () => {
      window.removeEventListener('resize', updateIsMobile);
      window.removeEventListener('orientationchange', updateIsMobile);
    };
  }, []);

  return isMobile;
};

export default function BookStore() {
  const [items, setItems] = useState([]); // New universal items system
  const [stock, setStock] = useState({});
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [storeSettings, setStoreSettings] = useState(defaultStoreSettings);
  const [waitlistedProducts, setWaitlistedProducts] = useState(new Set());
  const [waitlistDialog, setWaitlistDialog] = useState(null); // { itemId, itemName }
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = result
  const [user, setUser] = useState(null);
  const [promoGroups, setPromoGroups] = useState([]);
  const [regularItems, setRegularItems] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false); // Prevent multiple loads
  const [compositionByItemId, setCompositionByItemId] = useState({});
  const [targetMapCode, setTargetMapCode] = useState(null);
  const [targetMapGroup, setTargetMapGroup] = useState(null);
  const [targetMapAddresses, setTargetMapAddresses] = useState([]);
  const [selectedTargetAddressId, setSelectedTargetAddressId] = useState(null);
  const [targetMapError, setTargetMapError] = useState('');
  const [targetMapLoading, setTargetMapLoading] = useState(false);
  const targetPriceOverrides = useMemo(() => {
    if (!targetMapGroup?.target_special_prices || !Array.isArray(targetMapGroup.target_special_prices)) {
      return new Map();
    }
    return new Map(
      targetMapGroup.target_special_prices
        .map((entry) => [entry.item_id, Number(entry.price)])
        .filter(([_, price]) => Number.isFinite(price))
    );
  }, [targetMapGroup]);
  const targetSpecialItemIds = useMemo(() => {
    if (!targetMapGroup?.target_special_prices || !Array.isArray(targetMapGroup.target_special_prices)) {
      return new Set();
    }
    return new Set(targetMapGroup.target_special_prices.map((entry) => entry.item_id).filter(Boolean));
  }, [targetMapGroup]);
  const targetSpecialMetaById = useMemo(() => {
    if (!targetMapGroup?.target_special_prices || !Array.isArray(targetMapGroup.target_special_prices)) {
      return new Map();
    }
    return new Map(
      targetMapGroup.target_special_prices
        .filter((entry) => entry?.item_id)
        .map((entry) => [entry.item_id, entry])
    );
  }, [targetMapGroup]);
  const targetSpecialFallbackEmoji = targetMapGroup?.target_special_price_wait_emoji || '';
  const hasBootstrappedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const shimmerTimerRef = useRef(null);
  const shimmerResetRef = useRef(null);
  const mobileTargetPagerRef = useRef(null);
  const [isCartShimmering, setIsCartShimmering] = useState(false);
  const [mobileTargetView, setMobileTargetView] = useState('map');
  const isMobile = useIsMobile();
  
  // Helper: HEX -> RGBA with alpha
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${alpha})`;
    let h = hex.trim();
    if (h.startsWith('#')) h = h.slice(1);
    if (h.length === 3) {
      h = h.split('').map(c => c + c).join('');
    }
    if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(h.slice(0, 2), 16) || 0;
    const g = parseInt(h.slice(2, 4), 16) || 0;
    const b = parseInt(h.slice(4, 6), 16) || 0;
    const a = Math.max(0, Math.min(1, Number(alpha)));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };

  useEffect(() => {
    // Dev-only guard for React 18 StrictMode double-invocation
    if (import.meta.env?.DEV && hasBootstrappedRef.current) return;
    hasBootstrappedRef.current = true;
    checkAuthAndLoadData();
  }, []); // Only run once on mount (guarded for StrictMode)

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const codeParam = params.get('targetmap');
    if (codeParam) {
      setTargetMapCode(codeParam.trim());
    }
  }, []);

  useEffect(() => {
    if (!targetMapCode) {
      setTargetMapGroup(null);
      setTargetMapAddresses([]);
      setSelectedTargetAddressId(null);
      return;
    }
    let cancelled = false;
    const loadTargetMap = async () => {
      try {
        setTargetMapLoading(true);
        setTargetMapError('');
        const response = await PublicTargetMap.getAddressesByCode(targetMapCode);
        if (cancelled) return;
        setTargetMapGroup(response.group || null);
        const addresses = Array.isArray(response.addresses) ? response.addresses : [];
        setTargetMapAddresses(addresses);
        if (addresses.length > 0) {
          const readyFallback = addresses.find((addr) => !addr.has_paid_orders)?.id || addresses[0].id;
          setSelectedTargetAddressId(readyFallback);
        } else {
          setSelectedTargetAddressId(null);
        }
        if (response.group) {
          trackEvent(`targetmap_${targetMapCode}_open`, {
            code: targetMapCode,
            group: response.group.name,
            addresses: addresses.length,
          });
        }
      } catch (error) {
        if (cancelled) return;
        setTargetMapGroup(null);
        setTargetMapAddresses([]);
        setSelectedTargetAddressId(null);
        setTargetMapError(error.message || 'Не удалось загрузить целевые адреса');
      } finally {
        if (!cancelled) {
          setTargetMapLoading(false);
        }
      }
    };
    loadTargetMap();
    return () => { cancelled = true; };
  }, [targetMapCode]);

  const checkAuthAndLoadData = async () => {
    console.log("[BookStore] checkAuthAndLoadData called");
    
    // Prevent multiple calls
    if (dataLoaded) {
      console.log("[BookStore] Skipping - already loaded");
      return;
    }
    
    if (isLoading || isLoadingRef.current) {
      console.log("[BookStore] Skipping - already loading");
      return;
    }
    
    try {
      console.log("[BookStore] Setting user to null");
      setUser(null);
      
      console.log("[BookStore] Setting isAuthenticated to false");
      setIsAuthenticated(false);
      
      console.log("[BookStore] Starting to load store data");
      // Load store data
      await loadStoreData();
      console.log("[BookStore] loadStoreData completed");
    } catch (error) {
      console.error("[BookStore] Error in checkAuthAndLoadData:", error);
    }
  };

  const loadStoreData = async () => {
    console.log("[BookStore] loadStoreData started");
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsLoading(true);
    console.log("[BookStore] isLoading set to true");
    
    try {
      console.log("[BookStore] Starting API calls...");
      
      // Load data with individual error handling
      let itemsData = [], settingsData = [], movements = [], promoGroupsData = [];
      
      try {
        // Load active items from new universal system (only allowed for sale)
        itemsData = await PublicItems.filter({ is_active: true, is_allowed_for_sale: true });
        console.log("[BookStore] Items loaded:", itemsData.length);
      } catch (error) {
        console.error("[BookStore] Error loading items:", error);
      }
      
      try {
        settingsData = await PublicAppSettings.list();
        console.log("[BookStore] Settings loaded:", settingsData.length);
      } catch (error) {
        console.error("[BookStore] Error loading settings:", error);
      }
      
      // Загрузка остатков перенесена после применения настроек
      
      try {
        promoGroupsData = await PublicPromoGroup.filter({ is_active: true });
        console.log("[BookStore] Promo groups loaded:", promoGroupsData.length);
      } catch (error) {
        console.error("[BookStore] Error loading promo groups:", error);
      }
      
      console.log("[BookStore] API calls completed:", {
        itemsCount: itemsData?.length || 0,
        settingsCount: settingsData?.length || 0,
        movementsCount: movements?.length || 0,
        promoGroupsCount: promoGroupsData?.length || 0
      });

      const remoteSettings = settingsData.length > 0 ? settingsData[0]?.store_settings : {};
      const finalSettings = {
        ...defaultStoreSettings,
        ...(remoteSettings || {}),
      };
      if (!Array.isArray(finalSettings.hero_features) || finalSettings.hero_features.length === 0) {
        finalSettings.hero_features = defaultStoreSettings.hero_features;
      }
      if (!Array.isArray(finalSettings.storefront_warehouse_ids)) {
        finalSettings.storefront_warehouse_ids = [];
      }
      setStoreSettings(finalSettings);
      
      // ============ Загрузка остатков с учётом настроек ============
      let stockResponse = null;
      try {
        const warehouseIds = Array.isArray(finalSettings.storefront_warehouse_ids) ? finalSettings.storefront_warehouse_ids : [];
        if (warehouseIds.length > 0) {
          stockResponse = await PublicStockBalance.list({ warehouse_ids: warehouseIds.join(',') });
        } else {
          stockResponse = { stock: [] };
        }
        const stockArray = Array.isArray(stockResponse?.stock) ? stockResponse.stock : [];
        console.log("[BookStore] Stock balances loaded:", stockArray.length);

        // Построение карты остатков по item_id с фильтрацией по складам и суммированием
        const allowedWarehouseIds = warehouseIds;
        const stockMap = {};
        for (const row of stockArray) {
          if (!row || !row.item_id) continue;
          if (allowedWarehouseIds.length > 0 && row.warehouse_id && !allowedWarehouseIds.includes(row.warehouse_id)) continue;
          const qty = Number(row.quantity) || 0;
          stockMap[row.item_id] = (stockMap[row.item_id] || 0) + qty;
        }
        console.log("[BookStore] Final stock map (stockMap):", stockMap);
        setStock(stockMap);
      } catch (error) {
        console.error("[BookStore] Error loading stock balances:", error);
        setStock({});
      }
      setItems(itemsData);

      // Group items by promo groups
      const promoGroupsMap = new Map();
      const regularItems = [];

      for (const item of itemsData) {
        if (item.promo_group_ids && item.promo_group_ids.length > 0) {
          // Add item to each of its promo groups
          for (const groupId of item.promo_group_ids) {
            const group = promoGroupsData.find(g => g.id === groupId);
            if (group) {
              if (!promoGroupsMap.has(groupId)) {
                promoGroupsMap.set(groupId, { group, items: [] });
              }
              promoGroupsMap.get(groupId).items.push(item);
            }
          }
        } else {
          regularItems.push(item);
        }
      }

      // Sort promo groups by priority and set state
      const sortedPromoGroups = Array.from(promoGroupsMap.values())
        .sort((a, b) => a.group.priority - b.group.priority);

      setPromoGroups(sortedPromoGroups);
      setRegularItems(regularItems);

    } catch (error) {
      console.error("Error loading store data:", error);
      setStoreSettings(defaultStoreSettings);
    } finally {
        console.log("[BookStore] loadStoreData finished");
        setIsLoading(false);
        setDataLoaded(true); // Mark as loaded to prevent re-loading
        isLoadingRef.current = false;
    }
  };

  const targetMapMode = Boolean(targetMapGroup && targetMapAddresses.length > 0);
  const hideStorefrontHero = Boolean(targetMapMode && targetMapGroup?.hide_storefront_hero);
  const selectedTargetAddress = useMemo(() => {
    if (!targetMapAddresses.length || !selectedTargetAddressId) return null;
    return targetMapAddresses.find((addr) => addr.id === selectedTargetAddressId) || null;
  }, [targetMapAddresses, selectedTargetAddressId]);

  useEffect(() => {
    if (!targetMapMode || !selectedTargetAddress?.has_paid_orders) return;
    const nextAvailable = targetMapAddresses.find((address) => !address.has_paid_orders);
    if (nextAvailable) {
      setSelectedTargetAddressId(nextAvailable.id);
    }
  }, [targetMapAddresses, targetMapMode, selectedTargetAddress]);

  useEffect(() => {
    if (!targetMapMode || targetPriceOverrides.size === 0) return;
    setCart((prev) =>
      prev.map((cartItem) =>
        targetPriceOverrides.has(cartItem.id)
          ? { ...cartItem, price: targetPriceOverrides.get(cartItem.id) }
          : cartItem
      )
    );
  }, [targetMapMode, targetPriceOverrides]);

  const lockedShipment = useMemo(() => {
    if (!targetMapMode || !selectedTargetAddress || !targetMapGroup) {
      return null;
    }
    return {
      type: 'target_point',
      data: {
        target_address_id: selectedTargetAddress.id,
        target_group_code: targetMapGroup.code,
        name: selectedTargetAddress.name,
        emoji_wait: selectedTargetAddress.emoji_wait,
        emoji_ready: selectedTargetAddress.emoji_ready,
        coordinates: {
          latitude: Number(selectedTargetAddress.latitude),
          longitude: Number(selectedTargetAddress.longitude)
        },
        price: 0
      }
    };
  }, [targetMapMode, selectedTargetAddress, targetMapGroup]);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(cartItem => cartItem.id === item.id);
      if (existing) {
        return prev.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, cartQuantity: cartItem.cartQuantity + 1 }
            : cartItem
        );
      } else {
        return [...prev, { ...item, cartQuantity: 1 }];
      }
    });
  };

  const updateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      setCart(prev => prev.filter(cartItem => cartItem.id !== itemId));
    } else {
      setCart(prev => prev.map(cartItem =>
        cartItem.id === itemId
          ? { ...cartItem, cartQuantity: newQuantity }
          : cartItem
      ));
    }
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => {
      const price = Number(item.price);
      const qty = Number(item.cartQuantity) || 0;
      return sum + (Number.isFinite(price) ? price * qty : 0);
    }, 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((sum, item) => sum + item.cartQuantity, 0);
  };

  const effectivePromoGroups = useMemo(() => {
    return targetMapMode ? [] : promoGroups;
  }, [targetMapMode, promoGroups]);

  const effectiveRegularItems = useMemo(() => {
    const onlySpecialPriceItems = Boolean(targetMapGroup?.show_only_special_price_items);
    const completeBySpecialItems = Boolean(targetMapGroup?.complete_address_when_all_special_items_ordered);
    const orderedAtSelectedAddress = new Set(selectedTargetAddress?.ordered_special_item_ids || []);
    const base = targetMapMode && completeBySpecialItems
      ? items.filter((item) => targetSpecialItemIds.has(item.id) && !orderedAtSelectedAddress.has(item.id))
      : targetMapMode && onlySpecialPriceItems
      ? items.filter((item) => targetPriceOverrides.has(item.id))
      : targetMapMode
        ? items
        : regularItems;
    return base.map((item) => {
      const override = targetPriceOverrides.get(item.id);
      return override !== undefined ? { ...item, price: override } : item;
    });
  }, [items, regularItems, selectedTargetAddress, targetMapGroup, targetMapMode, targetPriceOverrides, targetSpecialItemIds]);

  const filteredRegularItems = effectiveRegularItems.filter(item => {
    const matchesSearch = !searchTerm ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const mobileTargetItems = useMemo(() => {
    if (!targetMapMode) return [];
    return effectiveRegularItems.filter((item) => targetPriceOverrides.has(item.id));
  }, [effectiveRegularItems, targetMapMode, targetPriceOverrides]);

  const selectedAddressOrderedCount = selectedTargetAddress?.ordered_special_item_ids?.length || 0;
  const targetSpecialItemsTotal = targetSpecialItemIds.size;

  useEffect(() => {
    if (!targetMapMode || !targetMapGroup?.complete_address_when_all_special_items_ordered) return;
    const allowedItemIds = new Set(effectiveRegularItems.map((item) => item.id));
    setCart((prev) => prev.filter((cartItem) => allowedItemIds.has(cartItem.id)));
  }, [effectiveRegularItems, targetMapGroup, targetMapMode]);

  const setMobileTargetPanel = (view) => {
    setMobileTargetView(view);
    const pager = mobileTargetPagerRef.current;
    if (!pager) return;
    const left = view === 'items' ? pager.clientWidth : 0;
    pager.scrollTo({ left, behavior: 'smooth' });
    window.dispatchEvent(new Event('resize'));
  };

  const handleMobileTargetScroll = () => {
    const pager = mobileTargetPagerRef.current;
    if (!pager) return;
    const nextView = pager.scrollLeft > pager.clientWidth / 2 ? 'items' : 'map';
    setMobileTargetView(nextView);
  };

  // Filter promo group items by search
  const filteredPromoGroups = effectivePromoGroups.map(({ group, items }) => ({
    group,
    items: items.filter(item => {
      const matchesSearch = !searchTerm ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    })
  })).filter(({ items }) => items.length > 0); // Only show groups with items

  const getItemComponents = (itemId) => compositionByItemId[itemId] || [];

  // Загрузим состав для всех bundle после загрузки items
  useEffect(() => {
    const loadCompositions = async () => {
      try {
        const bundleItems = (items || []).filter(it => it.type === 'bundle' && it.show_composition_in_storefront !== false);
        const newMap = {};
        for (const bundle of bundleItems) {
          const components = await PublicProductComponent.filter({ product_id: bundle.id });
          const mapped = (components || []).map(c => {
            if (c.child_type === 'book_edition') {
              return {
                id: c.book_edition_id,
                title: c.book_title || c.child_name || 'Книга',
                editionName: c.child_metadata?.edition_name || c.child_name,
                quantity: c.quantity || 1,
              };
            }
            return {
              id: c.single_product_id,
              title: c.child_name || 'Товар',
              editionName: null,
              quantity: c.quantity || 1,
            };
          });
          newMap[bundle.id] = mapped;
        }
        if (Object.keys(newMap).length > 0) {
          setCompositionByItemId(prev => ({ ...prev, ...newMap }));
        }
      } catch (e) {
        console.error('[BookStore] Failed to load compositions:', e);
      }
    };
    if (items && items.length > 0) {
      loadCompositions();
    }
  }, [items]);
  
  // Унифицированное получение изображений товара (учитывает legacy и metadata)
  const getItemImages = (item) => {
    if (!item) return [];
    if (Array.isArray(item.images) && item.images.length > 0) return item.images;
    if (Array.isArray(item.metadata?.images) && item.metadata.images.length > 0) return item.metadata.images;
    const cover = item.cover_image_url || item.metadata?.cover_image_url;
    if (cover) return [{ url: cover, alt: item.name, order: 0 }];
    return [];
  };
  
  const isItemInStock = (itemId) => {
    const item = items.find(entry => entry.id === itemId);
    const type = item?.type;
    if (type === 'simple_product' && item?.is_stock_control_enabled === false) {
      return true;
    }
    if (type === 'simple_product' || type === 'book_edition') {
      const itemStock = stock[itemId] || 0;
      return itemStock > 0;
    }

    // Для наборов (bundle) проверяем наличие всех компонентов
    if (type === 'bundle') {
      const components = compositionByItemId[itemId] || [];
      if (components.length === 0) {
        return false; // Нет компонентов - бандл недоступен
      }
      // Проверяем, что все компоненты есть в достаточном количестве
      for (const component of components) {
        const componentStock = stock[component.id] || 0;
        const requiredQuantity = component.quantity || 1;
        if (componentStock < requiredQuantity) {
          return false; // Недостаточно одного из компонентов
        }
      }
      return true; // Все компоненты есть в достаточном количестве
    }

    return false;
  };

  const handleOrderComplete = async (orderData) => {
    try {
      const shipmentDetailsPayload = lockedShipment || orderData.shipment_details;
      if (!shipmentDetailsPayload) {
        throw new Error('shipment_details_missing');
      }
      const shippingPrice = Number(shipmentDetailsPayload?.data?.price) || 0;

      const payload = {
        order_number: `WEB-${Date.now()}`,
        customer_id: '',
        customer_name: orderData.customer_name,
        customer_email: orderData.customer_email,
        customer_phone: orderData.customer_phone,
        items: cart.map(cartItem => ({
          product_id: cartItem.id,
          quantity: cartItem.cartQuantity
        })),
        total_amount: getCartTotal() + shippingPrice,
        status: 'ordered',
        payment_method: orderData.payment_method,
        shipment_details: shipmentDetailsPayload,
        notes: orderData.notes || 'Заказ через публичную витрину',
        order_date: new Date().toISOString().split('T')[0],
        // consent-spec.md §4.2: согласие пишется в одной транзакции с заказом.
        consent: orderData.consent || null,
      };

      if (lockedShipment && selectedTargetAddress) {
        payload.target_address_id = selectedTargetAddress.id;
        payload.target_group_code = targetMapGroup?.code || null;
      }

      const order = await PublicCheckout.createOrder({
        ...payload
      });

      setCart([]);
      setShowCheckout(false);

      // Redirect to order page with payment flag for T-Bank
      const redirectUrl = orderData.payment_method === 'tbank' 
        ? `/order/${order.id}?go-to-payment=true`
        : `/order/${order.id}`;
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error creating order:', error);
      
      // Парсим ошибку и показываем дружелюбное сообщение
      let friendlyError = 'Произошла ошибка при создании заказа. Попробуйте еще раз.';
      
      if (error.message) {
        if (error.message.includes('Invalid customer email format')) {
          friendlyError = 'Пожалуйста, проверьте правильность email адреса.';
        } else if (error.message.includes('product_id is required')) {
          friendlyError = 'Ошибка: товар не найден. Попробуйте обновить страницу.';
        } else if (error.message.includes('quantity')) {
          friendlyError = 'Ошибка: некорректное количество товара.';
        } else if (error.message.includes('not found')) {
          friendlyError = 'Ошибка: один из товаров недоступен. Попробуйте обновить корзину.';
        } else if (error.message.includes('shipment_details_missing')) {
          friendlyError = 'Ошибка: не выбран способ доставки.';
        }
      }
      
      alert(friendlyError);
      throw error; // Пробрасываем ошибку дальше для обработки в CheckoutModal
    }
  };

  const handleJoinWaitlist = (itemId, itemName) => {
    if (waitlistedProducts.has(itemId)) return;
    setWaitlistDialog({ itemId, itemName });
  };

  // Сам сабмит происходит внутри диалога, который вызывает этот колбек.
  // Согласие на ПД (consent-spec.md) пишется бэкендом до создания записи.
  const submitWaitlist = async ({ email, consent }) => {
    if (!waitlistDialog) return;
    const { itemId, itemName } = waitlistDialog;
    await PublicWaitlist.create({
      item_id: itemId,
      customer_email: email,
      status: 'waiting',
      consent
    });
    setWaitlistedProducts(prev => new Set(prev).add(itemId));
    alert(`Вы добавлены в список ожидания для "${itemName}". Уведомление придёт на ${email}.`);
  };

  const fontClass = fontClassMap[storeSettings.font_family] || 'font-inter';

  useEffect(() => {
    console.log("[BookStore] Render state:", { isAuthenticated, isLoading, dataLoaded });
  }, [isAuthenticated, isLoading, dataLoaded]);

  // Безопасный рендер описания с поддержкой ссылок и <br>
  const escapeHtml = (input) => String(input || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const sanitizeHeroDescription = (raw) => {
    const source = String(raw || '');
    try {
      if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(source, 'text/html');
        const walk = (node) => {
          if (!node) return '';
          if (node.nodeType === Node.TEXT_NODE) return escapeHtml(node.nodeValue);
          if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName.toLowerCase();
            if (tag === 'br') return '<br/>';
            if (tag === 'a') {
              const href = (node.getAttribute('href') || '').trim();
              if (!/^https?:\/\//i.test(href) && !/^mailto:/i.test(href)) {
                return escapeHtml(node.textContent || '');
              }
              const text = escapeHtml(node.textContent || '');
              const safeHref = href.replace(/\"/g, '%22');
              return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer">${text}</a>`;
            }
            return escapeHtml(node.textContent || '');
          }
          return '';
        };
        let out = '';
        for (const child of Array.from(doc.body.childNodes || [])) out += walk(child);
        return out;
      }
    } catch {}
    const escaped = escapeHtml(source.replace(/\r\n|\r|\n/g, '\n'));
    const linkified = escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1<\/a>');
    return linkified.replace(/\n/g, '<br/>' );
  };

  const renderHeroBlocks = (raw, settings) => {
    const blocks = parseHeroBlocks(raw, {
      primaryColor: settings.primary_color,
      accentColor: settings.button_background,
    });
    if (!blocks) {
      return (
        <div
          className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto"
          dangerouslySetInnerHTML={{ __html: sanitizeHeroDescription(raw || '') }}
        />
      );
    }

    const normalizeLink = (text) => {
      const trimmed = text.trim();
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      if (/^t\.me\/\S+$/i.test(trimmed)) return `https://${trimmed}`;
      if (/^@\w+/i.test(trimmed)) return `https://t.me/${trimmed.replace(/^@/, '')}`;
      return null;
    };

    const extractButton = (text) => {
      // Markdown-style link inside parentheses: ([Label](url))
      const mdMatch = text.match(/\(\s*\[([^\]]+)\]\(([^)]+)\)\s*\)/);
      if (mdMatch) {
        const label = mdMatch[1].trim();
        const hrefRaw = mdMatch[2].trim();
        const hrefMd = normalizeLink(hrefRaw) || (/^https?:\/\//i.test(hrefRaw) ? hrefRaw : null);
        if (hrefMd) return { href: hrefMd, label: label || hrefMd };
      }

      const cleaned = text
        .replace(/\[accent\]/g, '')
        .replace(/\[\/accent\]/g, '')
        .replace(/\[color=[^\]]+\]/g, '')
        .replace(/\[\/color\]/g, '')
        .trim();
      const href = normalizeLink(cleaned);
      if (!href) return null;
      return { href, label: cleaned.startsWith('@') ? cleaned : cleaned.replace(/^https?:\/\//, '') };
    };

    const linkify = (text, accent) => {
      const escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      const withAccent = escaped
        .replace(/\[accent\]([\s\S]*?)\[\/accent\]/g, `<span style="color:${accent}">$1</span>`) 
        .replace(/\[color=([^\]]+)\]([\s\S]*?)\[\/color\]/g, '<span style="color:$1">$2</span>');
      return withAccent.replace(/(https?:\/\/[^\s<]+)/g, `<a href="$1" target="_blank" rel="noopener noreferrer" style="color:${accent}">$1</a>`);
    };

    const renderParagraph = (p, b, key) => {
      const btn = extractButton(p);
      if (btn) {
        return (
          <div key={key}>
            <a
              href={btn.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-semibold shadow-sm transition hover:opacity-90"
              style={{ backgroundColor: b.colors.accent, color: '#0f172a' }}
            >
              {btn.label}
            </a>
          </div>
        );
      }
      return <div key={key} dangerouslySetInnerHTML={{ __html: linkify(p, b.colors.accent) }} />;
    };

    const renderLightCard = (b, key) => (
      <div key={key} className="rounded-xl p-6 shadow-sm border" style={{ backgroundColor: b.colors.bg, color: b.colors.text }}>
        {b.title && <div className="font-semibold text-lg mb-2" style={{ color: b.colors.text }}>{b.title}</div>}
        <div className="space-y-2" style={{ color: b.colors.text }}>
          {b.paragraphs.map((p, i) => renderParagraph(p, b, i))}
        </div>
      </div>
    );

    const renderDarkCard = (b, key) => (
      <div key={key} className="rounded-xl p-6" style={{ backgroundColor: b.colors.bg }}>
        {b.title && <div className="font-bold text-xl mb-3" style={{ color: b.colors.accent }}>{b.title}</div>}
        <div className="space-y-2" style={{ color: b.colors.text }}>
          {b.paragraphs.map((p, i) => renderParagraph(p, b, i))}
        </div>
      </div>
    );

    const rows = [];
    let buffer = [];
    const flushBuffer = () => {
      if (!buffer.length) return;
      rows.push({ kind: 'grid', blocks: buffer });
      buffer = [];
    };
    blocks.forEach((b) => {
      if (b.type === 'wide') {
        flushBuffer();
        rows.push({ kind: 'wide', block: b });
      } else {
        buffer.push(b);
      }
    });
    flushBuffer();

    return (
      <div className="w-full mb-8 max-w-7xl mx-auto text-left space-y-6">
        {rows.map((row, rowIdx) => {
          if (row.kind === 'wide') {
            return renderDarkCard(row.block, `wide-${rowIdx}`);
          }
          const lights = row.blocks.filter((b) => b.type === 'light');
          const darks = row.blocks.filter((b) => b.type === 'dark');
          return (
            <div key={`grid-${rowIdx}`} className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                {lights.map((b, idx) => renderLightCard(b, `light-${rowIdx}-${idx}`))}
              </div>
              <div className="md:col-span-1 space-y-4">
                {darks.map((b, idx) => renderDarkCard(b, `dark-${rowIdx}-${idx}`))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Автоматический перелив кнопки корзины при наличии товаров
  useEffect(() => {
    const count = cart.reduce((sum, item) => sum + (Number(item.cartQuantity) || 0), 0);

    const clearTimers = () => {
      if (shimmerTimerRef.current) {
        clearTimeout(shimmerTimerRef.current);
        shimmerTimerRef.current = null;
      }
      if (shimmerResetRef.current) {
        clearTimeout(shimmerResetRef.current);
        shimmerResetRef.current = null;
      }
    };

    if (count <= 0) {
      clearTimers();
      setIsCartShimmering(false);
      return;
    }

    let cancelled = false;
    const schedule = () => {
      const delay = 1000 + Math.floor(Math.random() * 6000); // 1–7 сек
      shimmerTimerRef.current = setTimeout(() => {
        if (cancelled) return;
        setIsCartShimmering(true);
        shimmerResetRef.current = setTimeout(() => {
          setIsCartShimmering(false);
          if (!cancelled) schedule();
        }, 1100); // длительность сияния ~1.1с
      }, delay);
    };

    schedule();

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [cart]);
  
  // Show loading while checking authentication and loading data
  if (isAuthenticated === null || isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: `linear-gradient(to bottom right, ${defaultStoreSettings.gradient_color_from}, ${defaultStoreSettings.gradient_color_to})`}}
      >
        <div className="text-center">
          <BookOpen className="w-16 h-16 mx-auto mb-4 animate-pulse" style={{ color: defaultStoreSettings.primary_color }} />
          <p className="text-lg font-semibold text-slate-900">Загружаем книги...</p>
        </div>
      </div>
    );
  }

  const primaryColor = storeSettings.primary_color || defaultStoreSettings.primary_color;
  const cartItemsCount = cart.reduce((sum, i) => sum + (Number(i.cartQuantity) || 0), 0);
  const totalFilteredItemsCount = filteredPromoGroups.reduce((sum, { items }) => sum + items.length, 0) + filteredRegularItems.length;

  return (
    <div
        className={`min-h-screen ${fontClass} relative font-manrope`}
        style={{ 
          background: `linear-gradient(to bottom right, ${storeSettings.gradient_color_from}, ${storeSettings.gradient_color_to})`,
          fontFamily: `Manrope,system-ui,sans-serif`
        }}
    >
      <style>{`
        /* Витрина: класс для Ubuntu с безопасными запасными шрифтами */
        .font-ubuntu { 
          font-family: Ubuntu,sans-serif,system-ui;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        /* Витрина: класс для Manrope (как на chaos.tb.ru) */
        .font-manrope {
          font-family: Manrope,system-ui,sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .bookstore-primary-btn {
          background-color: ${storeSettings.button_background || defaultStoreSettings.button_background};
          color: ${storeSettings.button_text_color || defaultStoreSettings.button_text_color};
          border: none;
          transition: all 0.2s ease-in-out;
        }
        .bookstore-primary-btn:hover {
          background-color: ${storeSettings.button_hover_background || defaultStoreSettings.button_hover_background};
          color: ${storeSettings.button_text_color || defaultStoreSettings.button_text_color};
        }
        .bookstore-outline-btn {
          background-color: transparent;
          color: ${storeSettings.button_background || defaultStoreSettings.button_background};
          border: 2px solid ${storeSettings.button_background || defaultStoreSettings.button_background};
          transition: all 0.2s ease-in-out;
        }
        .bookstore-outline-btn:hover {
          background-color: ${storeSettings.button_background || defaultStoreSettings.button_background};
          color: ${storeSettings.button_text_color || defaultStoreSettings.button_text_color};
        }

        /* Усиленный фон при наведении для непустой корзины */
        .cart-has-items:hover {
          filter: brightness(1.07);
          box-shadow: 0 16px 28px rgba(0, 0, 0, 0.2);
        }

        /* Анимация сияния поверх кнопки */
        .cart-shimmer {
          will-change: transform, box-shadow;
        }
        .cart-shimmer::after {
          content: '';
          position: absolute;
          inset: -20%;
          pointer-events: none;
          border-radius: inherit;
          background: linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 30%, rgba(255,255,255,0) 60%);
          transform: translateX(-120%) rotate(0.001deg);
          animation: cartShine 1.1s ease-in-out forwards;
        }
        @keyframes cartShine {
          0% { transform: translateX(-120%) rotate(0.001deg); }
          100% { transform: translateX(120%) rotate(0.001deg); }
        }
        
        /* Анимация сияния через внутренний слой, чтобы не обрезать бейдж */
        .cart-shimmer-layer {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          overflow: hidden;
          pointer-events: none;
          z-index: 0;
        }
        .cart-shimmer-layer.cart-shimmer::after {
          content: '';
          position: absolute;
          inset: -25%;
          border-radius: inherit;
          background: linear-gradient(120deg, rgba(255,255,255,0) 0%, var(--cart-shimmer-color, rgba(255,255,255,0.42)) 35%, rgba(255,255,255,0) 65%);
          transform: translateX(-120%) rotate(0.001deg);
          animation: cartShine 1.1s ease-in-out forwards;
        }
        @keyframes cartShine {
          0% { transform: translateX(-120%) rotate(0.001deg); }
          100% { transform: translateX(120%) rotate(0.001deg); }
        }
      `}</style>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-bold text-slate-900">{storeSettings.store_title}</h1>
              <p className="text-sm text-slate-600 mt-1">{storeSettings.store_tagline}</p>
            </div>
            {!hideStorefrontHero && (
              <div className="relative w-full md:w-auto md:min-w-[320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  placeholder="Поиск книг..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full md:w-80 max-w-full border-slate-300 focus:border-slate-400"
                />
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      {!hideStorefrontHero && (
        <section className="relative pt-8 md:pt-12 pb-16 md:pb-20 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h2
              className="mb-6"
              style={{
                fontSize: `${storeSettings.hero_title_font_size || 56}px`,
                fontWeight: storeSettings.hero_title_font_weight || '800',
                color: storeSettings.hero_title_color || '#0f172a'
              }}
            >
              {storeSettings.hero_title}
            </h2>
            <style>{`
              /* Усиленный визуальный стиль заголовка под скриншот */
              h2.mb-6 { letter-spacing: -0.01em; }
            `}</style>
            {renderHeroBlocks(storeSettings.hero_description || '', storeSettings)}
            <div className="flex items-center justify-center gap-6 text-sm text-slate-500 flex-wrap"
                 style={{ color: storeSettings.hero_features_color || '#64748b' }}>
              {(storeSettings.hero_features || []).map((feature, index) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div key={index} className="flex items-center gap-2">
                    {IconComponent ? <IconComponent className="w-4 h-4" /> : null}
                    <span>{feature.title}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {targetMapCode && (
        <section className="px-6 pb-10">
          <div className="max-w-7xl mx-auto space-y-4">
            {targetMapError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {targetMapError}
              </div>
            )}
            {targetMapMode ? (
              <>
                {isMobile ? (
                  <div className="space-y-3">
                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Выбранная точка</p>
                          <p className="truncate text-base font-semibold text-slate-900">
                            {selectedTargetAddress?.name || 'Выберите точку'}
                          </p>
                          <p className="text-xs text-slate-500">
                            Доступно {mobileTargetItems.length}
                            {targetSpecialItemsTotal > 0 ? ` из ${targetSpecialItemsTotal}` : ''} спец-товаров
                            {selectedAddressOrderedCount > 0 ? ` · заказано ${selectedAddressOrderedCount}` : ''}
                          </p>
                        </div>
                        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
                          <button
                            type="button"
                            onClick={() => setMobileTargetPanel('map')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${mobileTargetView === 'map' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                          >
                            Карта
                          </button>
                          <button
                            type="button"
                            onClick={() => setMobileTargetPanel('items')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${mobileTargetView === 'items' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
                          >
                            Товары
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-2">
                        {mobileTargetItems.length === 0 ? (
                          <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
                            Для этой точки нет доступных спец-товаров
                          </div>
                        ) : (
                          mobileTargetItems.slice(0, 2).map((item) => {
                            const meta = targetSpecialMetaById.get(item.id);
                            return (
                              <div key={item.id} className="grid grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2 py-2">
                                <span className="text-center text-lg">{meta?.emoji || targetSpecialFallbackEmoji || '•'}</span>
                                <span className="min-w-0 truncate text-sm font-medium text-slate-800">{item.name}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-8 shrink-0 bg-amber-600 px-2 text-xs hover:bg-amber-700"
                                  onClick={() => {
                                    addToCart(item);
                                    setMobileTargetPanel('items');
                                  }}
                                >
                                  Заказать
                                </Button>
                              </div>
                            );
                          })
                        )}
                        {mobileTargetItems.length > 2 && (
                          <button
                            type="button"
                            onClick={() => setMobileTargetPanel('items')}
                            className="rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-600"
                          >
                            Ещё {mobileTargetItems.length - 2} товаров
                          </button>
                        )}
                      </div>
                    </div>

                    <div
                      ref={mobileTargetPagerRef}
                      onScroll={handleMobileTargetScroll}
                      className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth rounded-xl"
                    >
                      <div className="w-full shrink-0 snap-start pr-1">
                        <TargetMapOverlay
                          group={targetMapGroup}
                          addresses={targetMapAddresses}
                          selectedAddressId={selectedTargetAddressId}
                          onSelectAddress={setSelectedTargetAddressId}
                        />
                      </div>
                      <div className="w-full shrink-0 snap-start pl-1">
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-slate-500">Товары точки</p>
                              <p className="font-semibold text-slate-900">{selectedTargetAddress?.name || 'Точка не выбрана'}</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {mobileTargetItems.length === 0 ? (
                              <div className="rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                                Все спец-товары для этой точки уже заказаны.
                              </div>
                            ) : (
                              mobileTargetItems.map((item) => {
                                const meta = targetSpecialMetaById.get(item.id);
                                const cartItem = cart.find((entry) => entry.id === item.id);
                                return (
                                  <div key={item.id} className="grid grid-cols-[40px_minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-slate-200 p-3">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-xl">
                                      {meta?.emoji || targetSpecialFallbackEmoji || '•'}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate font-medium text-slate-900">{item.name}</p>
                                      <p className="text-sm text-slate-500">₽{formatCurrency(item.price)}</p>
                                    </div>
                                    {cartItem ? (
                                      <div className="flex items-center gap-2">
                                        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => updateCartQuantity(item.id, cartItem.cartQuantity - 1)}>
                                          <Minus className="h-4 w-4" />
                                        </Button>
                                        <span className="min-w-[2ch] text-center font-semibold">{cartItem.cartQuantity}</span>
                                        <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => updateCartQuantity(item.id, cartItem.cartQuantity + 1)}>
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button type="button" className="bg-amber-600 hover:bg-amber-700" onClick={() => addToCart(item)}>
                                        Заказать
                                      </Button>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <TargetMapOverlay
                      group={targetMapGroup}
                      addresses={targetMapAddresses}
                      selectedAddressId={selectedTargetAddressId}
                      onSelectAddress={setSelectedTargetAddressId}
                    />
                    {selectedTargetAddress && (
                      <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
                        Точка доставки выбрана: <strong>{selectedTargetAddress.name}</strong>. Все заказы будут оформлены на эту геометку без возможности изменения адреса.
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div
                className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-4 py-6 text-center text-slate-500"
                data-testid="target-map-overlay"
              >
                {targetMapLoading ? 'Загружаем адреса группы…' : 'Для указанной группы нет активных адресов'}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Products Grid */}
      {!(isMobile && targetMapMode) && (
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-2xl font-bold text-slate-900" style={{ color: storeSettings.section_title_color || '#0f172a' }}>Наши книги</h3>
            <p className="text-slate-600">{totalFilteredItemsCount} товаров</p>
          </div>

          {/* Promo Groups */}
          {filteredPromoGroups.map(({ group, items }) => (
            <div key={group.id} className="mb-12">
              <h3 
                className="mb-6 text-center"
                style={{
                  fontSize: `${group.font_size}px`,
                  fontWeight: group.font_weight,
                  color: group.text_color,
                }}
              >
                {group.name}
              </h3>
              
              <div 
                className="grid gap-8"
                style={{
                  gridTemplateColumns: isMobile 
                    ? 'repeat(2, minmax(0, 1fr))' 
                    : `repeat(${group.columns || 4}, minmax(0, 1fr))`
                }}
              >
                {items.map((item) => {
                  const itemComponents = getItemComponents(item.id);
                  const cartItem = cart.find(cartItem => cartItem.id === item.id);
                  const itemImages = getItemImages(item);
                  const inStock = isItemInStock(item.id);
                  const isWaitlisted = waitlistedProducts.has(item.id);
                  const cols = isMobile ? 2 : (group.columns || 4);
                  const variant = group.card_variant || 'small';
                  const span = isMobile
                    ? (variant === 'large' ? 2 : 1)  // large = весь экран, остальные = половина
                    : (variant === 'medium' ? Math.min(2, cols) : (variant === 'large' ? Math.min(4, cols) : 1));

                  return (
                    <PromoProductCard
                      key={item.id}
                      style={{ gridColumn: span > 1 ? `span ${span} / span ${span}` : undefined }}
                      product={item}
                      productBooks={itemComponents}
                      cartItem={cartItem}
                      productImages={itemImages}
                      inStock={inStock}
                      isWaitlisted={isWaitlisted}
                      primaryColor={primaryColor}
                      storeSettings={storeSettings}
                      variant={variant}
                      isMobile={isMobile}
                      onAddToCart={() => addToCart(item)}
                      onUpdateQuantity={updateCartQuantity}
                      onJoinWaitlist={() => handleJoinWaitlist(item.id, item.name)}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Regular Items */}
          {filteredRegularItems.length > 0 && (
            <div>
              {filteredPromoGroups.length > 0 && (
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Все товары</h3>
              )}
              
              <div className={`grid gap-8 ${isMobile ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}>
                {filteredRegularItems.map((item) => {
                  const itemComponents = getItemComponents(item.id);
                  const cartItem = cart.find(cartItem => cartItem.id === item.id);
                  const itemImages = getItemImages(item);
                  const inStock = isItemInStock(item.id);
                  const isWaitlisted = waitlistedProducts.has(item.id);

                  return (
                    <ProductCard
                      key={item.id}
                      product={item}
                      productBooks={itemComponents}
                      cartItem={cartItem}
                      productImages={itemImages}
                      inStock={inStock}
                      isWaitlisted={isWaitlisted}
                      primaryColor={primaryColor}
                      storeSettings={storeSettings}
                      isMobile={isMobile}
                      onAddToCart={() => addToCart(item)}
                      onUpdateQuantity={updateCartQuantity}
                      onJoinWaitlist={() => handleJoinWaitlist(item.id, item.name)}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {totalFilteredItemsCount === 0 && !isLoading && (
            <div className="text-center py-20">
              <BookOpen className="w-24 h-24 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Товары не найдены</h3>
              <p className="text-slate-600">Попробуйте изменить поисковый запрос или проверьте настройки складов в разделе "Настройки"</p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Footer */}
      <div className="mt-16">
        <Footer />
      </div>
      
      

      {/* Floating Cart Button */}
      <div className="fixed bottom-8 right-8 z-20">
          <Button
              onClick={() => setShowCheckout(true)}
              className={`bookstore-primary-btn w-16 h-16 rounded-full shadow-lg hover:scale-105 relative ${cartItemsCount > 0 ? 'cart-has-items' : ''}`}
              style={{
                '--cart-shimmer-color': hexToRgba(
                  (storeSettings.button_hover_background || storeSettings.button_background || defaultStoreSettings.button_hover_background || defaultStoreSettings.button_background),
                  0.42
                )
              }}
              disabled={cart.length === 0}
          >
              <span className={`cart-shimmer-layer ${isCartShimmering ? 'cart-shimmer' : ''}`}></span>
              <ShoppingCart className="w-7 h-7 relative z-10" />
              {getCartItemsCount() > 0 && (
                  <Badge
                      className="absolute -top-1 -right-1 min-w-[26px] h-7 text-sm flex items-center justify-center rounded-full border-2 border-white z-10"
                      style={{ 
                        backgroundColor: storeSettings.button_background || defaultStoreSettings.button_background,
                        color: storeSettings.button_text_color || defaultStoreSettings.button_text_color
                      }}
                  >
                      {getCartItemsCount()}
                  </Badge>
              )}
          </Button>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        cart={cart}
        onUpdateQuantity={updateCartQuantity}
        onComplete={handleOrderComplete}
        total={getCartTotal()}
        lockedShipment={lockedShipment}
      />

      {/* Waitlist subscribe dialog with consent */}
      <WaitlistSubscribeDialog
        open={!!waitlistDialog}
        onClose={() => setWaitlistDialog(null)}
        itemName={waitlistDialog?.itemName}
        defaultEmail={user?.email || ''}
        onSubmit={submitWaitlist}
      />
    </div>
  );
}

// Add new PromoProductCard component
const PromoProductCard = ({ 
  product, 
  productBooks, 
  cartItem, 
  productImages, 
  inStock, 
  isWaitlisted, 
  primaryColor,
  storeSettings,
  variant,
  isMobile = false,
  onAddToCart,
  onUpdateQuantity,
  onJoinWaitlist,
  style
}) => {
  const isSmall = (variant || 'small') === 'small';
  const isLarge = variant === 'large';

  if (isSmall) {
    return (
      <div style={style}>
        <Card 
          className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm overflow-hidden flex flex-col"
        >
      <div className="relative">
        <ProductImageGallery
          images={productImages}
          productName={product.name}
          primaryColor={primaryColor}
        />
        <ProductLabels product={product} />
      </div>

      <CardContent className="p-6 flex flex-col flex-grow">
        <h4 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
          {product.name}
        </h4>

        {product.description && (
          <p className="text-sm text-slate-600 mb-4">
            {product.description}
          </p>
        )}

        {product.type === 'bundle' && productBooks.length > 0 && product.show_composition_in_storefront !== false && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 mb-2">В составе:</p>
            <div className="space-y-1">
              {productBooks.slice(0, 2).map((book, index) => (
                <p key={index} className="text-xs text-slate-600">
                  • {book.title} {book.editionName && `(${book.editionName})`} {book.quantity > 1 && `(${book.quantity} экз.)`}
                </p>
              ))}
              {productBooks.length > 2 && (
                <p className="text-xs text-slate-500">и еще {productBooks.length - 2}...</p>
              )}
            </div>
          </div>
        )}
        
        <div className="mt-auto pt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold text-slate-900">₽{formatCurrency(product?.price)}</p>
            </div>
          </div>

          <div className="w-full">
            {inStock ? (
              cartItem ? (
                <div className="flex items-center justify-center gap-2 w-full">
                  <Button
                    onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity - 1)}
                    className="bookstore-outline-btn h-10 w-10 p-0"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="font-semibold text-slate-900 min-w-[3ch] text-center text-lg">
                    {cartItem.cartQuantity}
                  </span>
                  <Button
                    onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity + 1)}
                    className="bookstore-outline-btn h-10 w-10 p-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={onAddToCart}
                  className="bookstore-primary-btn w-full h-10 font-medium"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  В корзину
                </Button>
              )
            ) : (
              <Button
                onClick={onJoinWaitlist}
                disabled={isWaitlisted}
                className={`bookstore-outline-btn w-full h-10 font-medium ${isMobile ? 'text-xs px-2' : ''}`}
              >
                {!isMobile && <Mail className="w-4 h-4 mr-2" />}
                {isWaitlisted ? (isMobile ? 'В ожидании' : 'В списке ожидания') : (isMobile ? 'Уведомить' : 'Сообщить о поступлении')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
        </Card>
      </div>
    );
  }

  // medium/large макет
  return (
    <div style={style}>
      <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
        <div className={isMobile ? "flex flex-col" : "grid grid-cols-12 gap-0"}>
          <div className={isMobile ? "" : (isLarge ? "col-span-7" : "col-span-6")}>
            <ProductImageGallery
              images={productImages}
              productName={product.name}
              primaryColor={primaryColor}
            />
          </div>
          <div className={isMobile ? "" : (isLarge ? "col-span-5" : "col-span-6")}>
            <CardContent className="p-6 h-full flex flex-col">
              <h4 className="text-2xl font-bold text-slate-900 mb-3 line-clamp-2">{product.name}</h4>
              {product.description && (
                <p className="text-sm text-slate-600 mb-4">{product.description}</p>
              )}
              {product.type === 'bundle' && productBooks.length > 0 && product.show_composition_in_storefront !== false && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-slate-500 mb-2">В составе:</p>
                  <div className="space-y-1">
                    {productBooks.slice(0, 4).map((book, index) => (
                      <p key={index} className="text-xs text-slate-600">• {book.title} {book.editionName && `(${book.editionName})`} {book.quantity > 1 && `(${book.quantity} экз.)`}</p>
                    ))}
                    {productBooks.length > 4 && (
                      <p className="text-xs text-slate-500">и еще {productBooks.length - 4}...</p>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-auto pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-3xl font-bold text-slate-900">₽{formatCurrency(product?.price)}</p>
                  </div>
                </div>
                <div className="w-full">
                  {inStock ? (
                    cartItem ? (
                      <div className="flex items-center justify-start gap-2 w-full">
                        <Button onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity - 1)} className="bookstore-outline-btn h-10 w-10 p-0"><Minus className="w-4 h-4" /></Button>
                        <span className="font-semibold text-slate-900 min-w-[3ch] text-center text-lg">{cartItem.cartQuantity}</span>
                        <Button onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity + 1)} className="bookstore-outline-btn h-10 w-10 p-0"><Plus className="w-4 h-4" /></Button>
                      </div>
                    ) : (
                      <Button onClick={onAddToCart} className="bookstore-primary-btn w-full h-10 font-medium">
                        <Plus className="w-4 h-4 mr-2" /> В корзину
                      </Button>
                    )
                  ) : (
                    <Button onClick={onJoinWaitlist} disabled={isWaitlisted} className={`bookstore-outline-btn w-full h-10 font-medium ${isMobile ? 'text-xs px-2' : ''}`}>
                      {!isMobile && <Mail className="w-4 h-4 mr-2" />} {isWaitlisted ? (isMobile ? 'В ожидании' : 'В списке ожидания') : (isMobile ? 'Уведомить' : 'Сообщить о поступлении')}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </div>
        </div>
      </Card>
    </div>
  );
}

// Product Card Component
const ProductCard = ({ 
  product, 
  productBooks, 
  cartItem, 
  productImages, 
  inStock, 
  isWaitlisted, 
  primaryColor,
  storeSettings, // Although storeSettings isn't directly used here, it's passed for consistency or future use within ProductLabels/Buttons
  isMobile = false,
  onAddToCart,
  onUpdateQuantity,
  onJoinWaitlist
}) => (
  <Card className="group hover:shadow-xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm overflow-hidden flex flex-col">
    <div className="relative">
      <ProductImageGallery
        images={productImages}
        productName={product.name}
        primaryColor={primaryColor}
      />
      <ProductLabels product={product} />
    </div>

    <CardContent className="p-6 flex flex-col flex-grow">
      <h4 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">
        {product.name}
      </h4>

      {product.description && (
        <p className="text-sm text-slate-500 mb-4">
          {product.description}
        </p>
      )}

      {product.type === 'bundle' && productBooks.length > 0 && product.show_composition_in_storefront !== false && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-slate-500 mb-2">В составе:</p>
          <div className="space-y-1">
            {productBooks.slice(0, 2).map((book, index) => (
              <p key={index} className="text-xs text-slate-600">
                • {book.title} {book.editionName && `(${book.editionName})`} {book.quantity > 1 && `(${book.quantity} экз.)`}
              </p>
            ))}
            {productBooks.length > 2 && (
              <p className="text-xs text-slate-500">и еще {productBooks.length - 2}...</p>
            )}
          </div>
        </div>
      )}
      
      <div className="mt-auto pt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-slate-900">₽{formatCurrency(product?.price)}</p>
          </div>
        </div>

        <div className="w-full">
          {inStock ? (
            cartItem ? (
              <div className="flex items-center justify-center gap-2 w-full">
                <Button
                  onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity - 1)}
                  className="bookstore-outline-btn h-10 w-10 p-0"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="font-semibold text-slate-900 min-w-[3ch] text-center text-lg">
                  {cartItem.cartQuantity}
                </span>
                <Button
                  onClick={() => onUpdateQuantity(product.id, cartItem.cartQuantity + 1)}
                  className="bookstore-outline-btn h-10 w-10 p-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                onClick={onAddToCart}
                className="bookstore-primary-btn w-full h-10 font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                В корзину
              </Button>
            )
          ) : (
            <Button
              onClick={onJoinWaitlist}
              disabled={isWaitlisted}
              className={`bookstore-outline-btn w-full h-10 font-medium ${isMobile ? 'text-xs px-2' : ''}`}
            >
              {!isMobile && <Mail className="w-4 h-4 mr-2" />}
              {isWaitlisted ? (isMobile ? 'В ожидании' : 'В списке ожидания') : (isMobile ? 'Уведомить' : 'Сообщить о поступлении')}
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Runtime API base resolver (shared with image URLs)
let RUNTIME_API_BASE = null;
async function getApiBase() {
  if (RUNTIME_API_BASE) return RUNTIME_API_BASE;
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (res.ok) {
      const cfg = await res.json();
      if (cfg && typeof cfg.API_BASE === 'string' && cfg.API_BASE.length > 0) {
        RUNTIME_API_BASE = cfg.API_BASE;
        return RUNTIME_API_BASE;
      }
    }
  } catch {}
  RUNTIME_API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
  return RUNTIME_API_BASE;
}

// Product Image Gallery Component
const ProductImageGallery = ({ images, productName, primaryColor }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [apiBase, setApiBase] = useState(null);

  useEffect(() => {
    getApiBase().then(setApiBase).catch(() => setApiBase(null));
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className="w-full h-64 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
        <BookOpen className="w-16 h-16" style={{ color: primaryColor }} />
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Helper function to ensure proper image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    // If it starts with /api (e.g. /api/v1/uploads/...), convert to absolute using runtime API base
    if (imageUrl.startsWith('/api')) {
      // Remove leading /api/v1 to avoid duplication with apiBase ending with /api/v1
      const suffix = imageUrl.replace(/^\/api\/v1/, '');
      return apiBase ? `${apiBase}${suffix}` : imageUrl;
    }
    
    // If it's just a filename, construct the full URL
    if (!imageUrl.startsWith('/')) {
      const base = apiBase || '/api/v1';
      return `${base}/uploads/files/${imageUrl}`;
    }
    
    return imageUrl;
  };

  const currentImage = images[currentImageIndex];
  const imageUrl = getImageUrl(currentImage?.url);

  return (
    <div className="relative group">
      <img
        src={imageUrl}
        alt={currentImage?.alt || productName}
        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => {
          console.error('Failed to load image:', imageUrl);
          e.target.style.display = 'none';
        }}
        onLoad={() => {
          console.log('Image loaded successfully:', imageUrl);
        }}
      />

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); prevImage(); }}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            <ChevronLeft className="w-4 h-4 text-slate-700" />
          </button>

          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); nextImage(); }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ backdropFilter: 'blur(4px)' }}
          >
            <ChevronRight className="w-4 h-4 text-slate-700" />
          </button>

          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex(index); }}
                className={`w-2 h-2 rounded-full ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/60'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
